import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Download, MoreHorizontal } from "lucide-react";
import {
  asText,
  DateRange,
  DateRangeDropdown,
  EmptyRow,
  formatCompact,
  formatCount,
  formatCurrency,
  formatRoas,
  LoadingRow,
  MetricCard,
  presetToDates,
  readMetric,
  StatusBadge,
  unwrapValue,
  useCxoResource,
} from "./shared";

// Campaigns list — GET /cxo-dashboard/campaigns/top (the table, same real
// endpoint the Overview page's own "Top Ad Campaigns" uses, just unfiltered/
// paginated here) and GET /cxo-dashboard/overview (the six summary cards,
// same shape as the Overview page's own Campaign Overview row).

const STATUS_OPTIONS = ["All", "ACTIVE", "PAUSED", "COMPLETED"];
const OBJECTIVE_OPTIONS = ["All", "Conversions", "Awareness", "Traffic", "Leads", "App Installs"];
const PLACEMENT_OPTIONS = ["All", "feed", "stories", "reels", "explore"];
const SORT_COLUMNS = ["name", "status", "spend", "reach", "outcome", "ctr"] as const;

const CampaignsList: React.FC = () => {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState<DateRange>("Last 7 Days");
  const { start_date, end_date } = useMemo(() => presetToDates(dateRange), [dateRange]);
  const baseParams = useMemo(() => ({ mode: "fixed", start_date, end_date }), [start_date, end_date]);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");
  const [objective, setObjective] = useState("All");
  const [placement, setPlacement] = useState("All");
  const [sortBy, setSortBy] = useState<(typeof SORT_COLUMNS)[number]>("spend");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(0);
  const limit = 10;

  const { data: overview, loading: overviewLoading } = useCxoResource<any>("/cxo-dashboard/overview", undefined, baseParams);
  const tableParams = useMemo(
    () => ({
      ...baseParams,
      sort_by: sortBy,
      order,
      limit: String(limit),
      offset: String(page * limit),
      ...(search ? { q: search } : {}),
      ...(status !== "All" ? { status } : {}),
      ...(objective !== "All" ? { objective } : {}),
      ...(placement !== "All" ? { placement } : {}),
    }),
    [baseParams, sortBy, order, page, search, status, objective, placement]
  );
  const { data: table, loading: tableLoading } = useCxoResource<any>("/cxo-dashboard/campaigns/top", undefined, tableParams);
  const items: any[] = Array.isArray(table?.items) ? table.items : [];
  const total = table?.total ?? items.length;
  const hasMore = !!table?.has_more;

  const handleSort = (col: (typeof SORT_COLUMNS)[number]) => {
    setPage(0);
    setOrder((prev) => (sortBy === col ? (prev === "asc" ? "desc" : "asc") : "desc"));
    setSortBy(col);
  };

  const handleExport = () => {
    const token = localStorage.getItem("authToken");
    const base = process.env.REACT_APP_BACKEND_URL || "https://api.connect.dalmlabs.com";
    const params = new URLSearchParams({
      ...baseParams,
      sort_by: sortBy,
      order,
      ...(search ? { q: search } : {}),
      ...(status !== "All" ? { status } : {}),
      ...(objective !== "All" ? { objective } : {}),
      ...(placement !== "All" ? { placement } : {}),
    });
    // A real file download, not another fetch -> JSON cycle — the browser's
    // own auth header can't ride along on a plain navigation, so the token
    // goes as a query param here only (GET /cxo-dashboard/campaigns/export,
    // confirmed CSV response).
    window.open(`${base}/cxo-dashboard/campaigns/export?${params.toString()}&access_token=${encodeURIComponent(token || "")}`, "_blank");
  };

  const campaigns = readMetric(overview?.data?.total_ad_campaigns ?? overview?.total_ad_campaigns);
  const spend = readMetric(overview?.data?.total_campaign_spend ?? overview?.total_campaign_spend);
  const impressions = readMetric(overview?.data?.total_impressions ?? overview?.total_impressions);
  const reach = readMetric(overview?.data?.total_reach ?? overview?.total_reach);
  const results = readMetric(overview?.data?.total_results ?? overview?.total_results);
  const costPerResult = readMetric(overview?.data?.avg_cost_per_result ?? overview?.avg_cost_per_result);

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">Campaigns</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Monitor performance across all your Meta campaigns.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <DateRangeDropdown value={dateRange} onChange={setDateRange} />
          <button
            type="button"
            onClick={handleExport}
            className="flex items-center gap-1.5 px-3.5 py-2 text-sm font-semibold text-white bg-gray-900 dark:bg-gray-100 dark:text-gray-900 rounded-lg hover:opacity-90"
          >
            <Download size={14} /> Export
          </button>
        </div>
      </div>

      {overviewLoading && !overview ? (
        <LoadingRow label="Loading campaign totals…" />
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          <MetricCard label="Total Campaigns" metric={campaigns} format={formatCount} empty="0" />
          <MetricCard label="Total Spend" metric={spend} format={formatCurrency} invert empty="₦0.00" />
          <MetricCard label="Total Impressions" metric={impressions} format={formatCompact} empty="0" />
          <MetricCard label="Total Reach" metric={reach} format={formatCompact} empty="0" />
          <MetricCard label="Results" metric={results} format={formatCount} empty="0" />
          <MetricCard label="Avg. Cost per Result" metric={costPerResult} format={formatCurrency} invert empty="₦0.00" />
        </div>
      )}

      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 sm:p-5">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(e) => {
                setPage(0);
                setSearch(e.target.value);
              }}
              placeholder="Search campaigns..."
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-500/20"
            />
          </div>
          <div className="flex items-center gap-2">
            {[
              { label: "Status", value: status, options: STATUS_OPTIONS, onChange: setStatus },
              { label: "Objective", value: objective, options: OBJECTIVE_OPTIONS, onChange: setObjective },
              { label: "Placement", value: placement, options: PLACEMENT_OPTIONS, onChange: setPlacement },
            ].map((f) => (
              <select
                key={f.label}
                value={f.value}
                onChange={(e) => {
                  setPage(0);
                  f.onChange(e.target.value);
                }}
                className="px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-700 dark:text-gray-200 capitalize"
              >
                {f.options.map((o) => (
                  <option key={o} value={o}>
                    {f.label}: {o}
                  </option>
                ))}
              </select>
            ))}
          </div>
        </div>

        {tableLoading && items.length === 0 ? (
          <LoadingRow label="Loading campaigns…" />
        ) : items.length === 0 ? (
          <EmptyRow label="No campaigns match these filters." />
        ) : (
          <>
            <div className="overflow-x-auto -mx-1">
              <table className="w-full min-w-[800px] text-sm">
                <thead>
                  <tr className="text-left text-[11px] font-semibold text-gray-400 dark:text-gray-500 tracking-wide">
                    <th className="font-semibold pb-2 pl-1">
                      <button type="button" onClick={() => handleSort("name")} className="hover:text-gray-600 dark:hover:text-gray-300">
                        CAMPAIGN NAME
                      </button>
                    </th>
                    <th className="font-semibold pb-2">
                      <button type="button" onClick={() => handleSort("status")} className="hover:text-gray-600 dark:hover:text-gray-300">
                        STATUS
                      </button>
                    </th>
                    <th className="font-semibold pb-2">OBJECTIVE</th>
                    <th className="font-semibold pb-2">
                      <button type="button" onClick={() => handleSort("spend")} className="hover:text-gray-600 dark:hover:text-gray-300">
                        SPEND
                      </button>
                    </th>
                    <th className="font-semibold pb-2">IMPRESSIONS</th>
                    <th className="font-semibold pb-2">
                      <button type="button" onClick={() => handleSort("reach")} className="hover:text-gray-600 dark:hover:text-gray-300">
                        REACH
                      </button>
                    </th>
                    <th className="font-semibold pb-2">
                      <button type="button" onClick={() => handleSort("outcome")} className="hover:text-gray-600 dark:hover:text-gray-300">
                        RESULTS
                      </button>
                    </th>
                    <th className="font-semibold pb-2">COST PER RESULT</th>
                    <th className="font-semibold pb-2">ROAS</th>
                    <th className="font-semibold pb-2 pr-1 text-right">ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, i) => {
                    const id = item.campaign_id || item.id;
                    const roasMetric = readMetric(item.roas ?? item.metrics?.roas);
                    const spendValue = unwrapValue(item.spend ?? item.metrics?.spend);
                    // `impressions` has no top-level field at all on the real
                    // campaign-list item (confirmed live) — only
                    // `metrics.impressions.value` — this was always reading
                    // undefined, so every row's Impressions column was blank
                    // regardless of whether the campaign actually had any.
                    const impressionsValue = unwrapValue(item.impressions ?? item.metrics?.impressions);
                    const reachValue = unwrapValue(item.reach ?? item.metrics?.reach);
                    const resultsValue = unwrapValue(item.results ?? item.outcome?.value ?? item.outcome ?? item.metrics?.results);
                    const cprValue = unwrapValue(item.cost_per_result ?? item.metrics?.cost_per_result);
                    return (
                      <tr key={id || i} className="border-t border-gray-50 dark:border-gray-800">
                        <td className="py-2.5 pl-1 whitespace-nowrap">
                          <button type="button" onClick={() => id && navigate(`/cxo/campaigns/${id}`)} className="font-medium text-indigo-600 dark:text-indigo-400 hover:underline text-left">
                            {asText(item.name ?? item.campaign_name)}
                          </button>
                        </td>
                        <td className="py-2.5">
                          <StatusBadge status={item.status} />
                        </td>
                        <td className="py-2.5 text-gray-600 dark:text-gray-300 capitalize">{asText(item.objective)}</td>
                        <td className="py-2.5 text-gray-600 dark:text-gray-300">{formatCurrency(spendValue, "₦0.00")}</td>
                        <td className="py-2.5 text-gray-600 dark:text-gray-300">{formatCompact(impressionsValue, "0")}</td>
                        <td className="py-2.5 text-gray-600 dark:text-gray-300">{formatCompact(reachValue, "0")}</td>
                        <td className="py-2.5 text-gray-600 dark:text-gray-300">{formatCount(resultsValue, "0")}</td>
                        <td className="py-2.5 text-gray-600 dark:text-gray-300">{formatCurrency(cprValue, "₦0.00")}</td>
                        <td className="py-2.5 font-semibold text-emerald-600 dark:text-emerald-400">{roasMetric.available ? formatRoas(roasMetric.value, "N/A") : "N/A"}</td>
                        <td className="py-2.5 pr-1 text-right">
                          <button type="button" onClick={() => id && navigate(`/cxo/campaigns/${id}`)} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800">
                            <MoreHorizontal size={16} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between mt-4 text-xs text-gray-400 dark:text-gray-500">
              <span>
                Showing {page * limit + 1}–{Math.min(page * limit + items.length, total)} of {total}
              </span>
              <div className="flex items-center gap-2">
                <button type="button" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} className="px-2.5 py-1 rounded-md border border-gray-200 dark:border-gray-700 disabled:opacity-40">
                  Previous
                </button>
                <button type="button" disabled={!hasMore} onClick={() => setPage((p) => p + 1)} className="px-2.5 py-1 rounded-md border border-gray-200 dark:border-gray-700 disabled:opacity-40">
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CampaignsList;
